<script lang="ts" context="module">
  import type { GroupMember } from "@/Common/ActivityFunctions";

  // Mirrors getSquadmateColor() in resources/assets/js/mpa.js so squadmate border
  // colors stay consistent between the group analytics chart and these avatars.
  const SQUADMATE_COLOR_PALETTE = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#bcbd22', '#17becf', '#393b79',
    '#637939', '#8c6d31', '#843c39', '#7b4173', '#5254a3',
    '#bd9e39', '#ad494a', '#a55194', '#3182bd', '#31a354',
    '#756bb1', '#636363', '#08519c', '#e7ba52',
  ];
  export function trophyColor(medal: number): string {
    if (medal === 3) return "text-[gold]";
    if (medal === 2) return "text-[silver]";
    return "text-[#CD7F32]"; // bronze
  }

  export function tidy(n: number): number {
    return Math.round(10 * Math.abs(n)) / 10;
  }

  // Short-form TP/HIE/PP values and the +/- change a breakthrough moved them by.
  export function signatureDeltas(breakthrough: NonNullable<GroupMember["breakthrough"]>) {
    const { signature: s, br_signature_changes: b } = breakthrough;
    return [
      { label: "TP", value: `${Math.round(s.ftp)}W`, delta: s.ftp - b.ftp, unit: "W" },
      { label: "HIE", value: `${tidy(s.atc / 1000)}kJ`, delta: (s.atc - b.atc) / 1000, unit: "kJ" },
      { label: "PP", value: `${Math.round(s.pp)}W`, delta: s.pp - b.pp, unit: "W" },
    ];
  }

  function hashUsername(username: string): number {
    let h = 0;
    for (let i = 0; i < (username || '').length; ++i) {
      h = ((h << 5) - h + username.charCodeAt(i)) | 0;
    }
    return Math.abs(h);
  }
  export function getSquadmateColor(username: string): string {
    return SQUADMATE_COLOR_PALETTE[hashUsername(username) % SQUADMATE_COLOR_PALETTE.length];
  }
</script>

<script lang="ts">
  import { onMount } from "svelte";
  import { scale } from "svelte/transition";
  import { backOut } from "svelte/easing";
  import { cn } from "@/lib/utils";
  import * as Avatar from "$lib/components/ui/avatar";
  import * as Popover from "$lib/components/ui/popover/index.js";
  import route from "@/Util/routes";
  import { getRequest } from "@/Util/utils";

  export let members: GroupMember[] = [];
  // Avatar diameter in pixels (including the colored border).
  export let size: number = 28;

  // Only animate avatars that appear *after* the initial render, so a member
  // showing up in real time (reactive refetch) pops in and draws attention,
  // while the avatars already present on first paint render statically.
  let mounted = false;
  onMount(() => { mounted = true; });
  $: introParams = mounted
    ? { duration: 500, start: 0.2, opacity: 0, easing: backOut }
    : { duration: 0 };

  // Per-member in-flight state for the squad-invite nudge, so the icon can disable
  // itself while a request is pending.
  let nudging: Record<string, boolean> = {};

  // Nudge a "Shared" (non-squadmate) member to join the dominant group squad. Sends a
  // bell notification server-side; re-invites are allowed so the icon re-enables after.
  async function nudge(member: GroupMember) {
    if (!member.invite_club || nudging[member.path]) return;
    nudging = { ...nudging, [member.path]: true };
    const xert = (window as any).Xert;
    try {
      const res = await window.fetch(route("ActivityController@nudgeSquadInvite"), {
        ...getRequest(),
        method: "post",
        body: JSON.stringify({ member_path: member.path, club_name: member.invite_club.name }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.result) {
        xert?.success?.(data.msg ?? "Invitation sent.");
      } else {
        xert?.warning?.(data?.msg ?? "Could not send invitation.");
      }
    } catch (e) {
      xert?.danger?.("Could not send invitation.");
    } finally {
      nudging = { ...nudging, [member.path]: false };
    }
  }
</script>

{#if members?.length}
  <div class="flex flex-wrap items-center gap-1.5">
    {#each members as member (member.path)}
      <Popover.Root portal="#shadcn-ui-dialog-target">
        <Popover.Trigger asChild let:builder>
          <button
            use:builder.action
            {...builder}
            type="button"
            in:scale|local={introParams}
            class="relative block shrink-0 overflow-visible rounded-full border-0 p-0 bg-transparent leading-none box-border cursor-pointer"
            style={`width:${size}px;height:${size}px;`}
          >
            <span
              class="block h-full w-full overflow-hidden rounded-full border-2 border-solid transition hover:brightness-90"
              style={`border-color:${getSquadmateColor(member.username)};`}
            >
              <Avatar.Root class="block h-full w-full overflow-hidden rounded-full">
                <Avatar.Image class="aspect-square h-full w-full object-cover" src={member.avatar} alt={member.display_name} />
                <Avatar.Fallback>{member.display_name?.[0]?.toUpperCase() ?? "?"}</Avatar.Fallback>
              </Avatar.Root>
            </span>
            {#if member.breakthrough}
              <span
                class="absolute -bottom-0.5 -right-0.5 flex items-center justify-center rounded-full bg-white shadow"
                style={`width:${Math.max(13, Math.round(size * 0.55))}px;height:${Math.max(13, Math.round(size * 0.55))}px;`}
                title="Breakthrough"
              >
                <i class={cn("fa-solid fa-trophy", trophyColor(member.breakthrough.medal))} style="font-size: 8px;" />
              </span>
            {/if}
          </button>
        </Popover.Trigger>
        <Popover.Content class="w-64 space-y-1">
          <div class="font-semibold leading-tight">{member.display_name}</div>

          {#if member.squad_name}
            <a
              class="block text-sm text-xerteal-600 hover:underline"
              href={member.squad_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {member.squad_name}
            </a>
          {:else if member.shared_only}
            <div class="flex items-center gap-1.5 text-sm text-gray-500">
              <span>Shared</span>
              {#if member.invite_club}
                <button
                  type="button"
                  class="inline-flex items-center justify-center border-0 bg-transparent p-0 cursor-pointer text-xerteal-600 hover:text-xerteal-700 disabled:opacity-50 disabled:cursor-default"
                  title={`Invite to ${member.invite_club.display_name}`}
                  aria-label={`Invite to ${member.invite_club.display_name}`}
                  on:click={() => nudge(member)}
                  disabled={nudging[member.path]}
                >
                  <i class="fa-solid fa-user-plus"></i>
                </button>
              {/if}
            </div>
          {/if}

          <a
            class="block text-sm text-xerteal-600 hover:underline"
            href={member.activity_url ?? `/activities/${member.path}?group_sharing=Group`}
            target="activity"
            rel="noopener noreferrer"
          >
            {member.name}
          </a>

          {#if member.breakthrough}
            <div class="flex items-center gap-2 pt-0.5 text-xs text-muted-foreground">
              <i class={cn("fa-regular fa-trophy", trophyColor(member.breakthrough.medal))} />
              {#each signatureDeltas(member.breakthrough) as d (d.label)}
                <span>
                  {d.label} <b class="text-foreground">{d.value}</b>
                  <span class={d.delta >= 0 ? "text-[#5CA907]" : "text-[#FF2222]"}
                    >({d.delta >= 0 ? "+" : "-"}{tidy(d.delta)}{d.unit})</span
                  >
                </span>
              {/each}
            </div>
          {/if}
        </Popover.Content>
      </Popover.Root>
    {/each}
  </div>
{/if}
